import React from 'react';
import layoutStyles from '../AdminSharedLayout.module.css';
import styles from '../AdminUserCfpSessions.module.css';
import loadingStyles from '../shared/Loading.module.css';
import BrandLogo from '../BrandLogo';
import {
  BackIcon,
  LogoutIcon,
  getAdminSessionInfo,
} from '../AdminShared/helpers';
import {
  canMarkCfpSessionDone,
  formatCfpSessionTimestamp,
  getSessionStatusMeta,
} from '../dashboard/schedule-cfp-session/cfpSessionStatus';

export { getAdminSessionInfo };

export const displayText = (value, fallback = '—') => String(value ?? '').trim() || fallback;

export const buildExpertNameMap = (experts) => {
  const map = Object.create(null);
  (experts || []).forEach((expert) => {
    const id = String(expert?.id ?? '').trim();
    if (id) map[id] = expert.name;
  });
  return map;
};

export const mapSessionRow = (row, expertNameById, index, statusCopy) => {
  const userName = displayText(row?.user_name);
  const topicName = displayText(row?.topic);
  const expertId = String(row?.expert_id ?? '').trim();

  return {
    key: row?.id ?? `${userName}-${topicName}-${index}`,
    row,
    userName,
    topicName,
    expertName: expertNameById[expertId] || expertId || '—',
    statusMeta: getSessionStatusMeta(row, statusCopy),
    canMarkDone: canMarkCfpSessionDone(row),
  };
};

export function TimestampCell({ value }) {
  const timestamp = formatCfpSessionTimestamp(value);
  if (!timestamp) {
    return <span className={styles['empty-value']}>—</span>;
  }

  return (
    <div className={styles['date-inner']}>
      <span className={styles['date-text']}>{timestamp.date}</span>
      {timestamp.time && <span className={styles['date-label']}>{timestamp.time}</span>}
    </div>
  );
}

export const CalendarIcon = () => (
  <svg
    className={styles['title-icon']}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <rect x="7" y="14" width="4" height="4" rx="0.5" />
  </svg>
);

export const EmptyIcon = () => (
  <svg
    className={styles['empty-icon']}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="9" y1="15" x2="15" y2="15" />
  </svg>
);

export const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2 5 5h-5V4zM8 13h8v2H8v-2zm0 4h8v2H8v-2z" />
  </svg>
);

export function CfpSessionsLoadingState() {
  return (
    <div className={styles['loading-shell']} aria-busy="true" aria-live="polite">
      <div className={loadingStyles['loading-container']}>
        <div className={loadingStyles['loading-spinner']}>
          <div className={loadingStyles['spinner-ring']} />
          <div className={loadingStyles['spinner-ring']} />
          <div className={loadingStyles['spinner-ring']} />
        </div>
        <p>Loading CFP sessions…</p>
      </div>
    </div>
  );
}

export function CfpSessionsHeader({ adminInfo, onBack, onLogout }) {
  return (
    <header className={layoutStyles['admin-header']} role="banner">
      <div className={layoutStyles['header-left']}>
        <BrandLogo decorative decoding="async" to="/admin-dashboard" />
        <div className={layoutStyles['header-info']}>
          <h1 className={layoutStyles['page-title']}>User CFP Sessions</h1>
          <p className={layoutStyles['page-description']}>
            Paid and completed CFP session topic selections
          </p>
        </div>
      </div>

      <div className={layoutStyles['header-right']}>
        <div className={layoutStyles['admin-info']}>
          <div className={layoutStyles['admin-details']}>
            <h2 className={layoutStyles['admin-name']}>{adminInfo.fullName}</h2>
            <span className={layoutStyles['admin-details-sep']} aria-hidden="true" />
            <p className={layoutStyles['admin-email']}>{adminInfo.email}</p>
          </div>
          <button
            type="button"
            className={layoutStyles['users-button']}
            onClick={onBack}
            aria-label="Back to onboarding users"
          >
            <BackIcon />
            Users
          </button>
          <button type="button" className={layoutStyles['logout-button']} onClick={onLogout}>
            <LogoutIcon />
            Log out
          </button>
        </div>
      </div>
    </header>
  );
}
